import { InputActionKeyBindingsMessage, InputActionMessage, InputActionTriggeredMessage, InputAxisValueMessage, RegisterInputActionMessage, ResponseRegisterInputActionMessage, UnregisterInputActionMessage } from "../../../messages/input/InputMessages";
import { ButtonSignal, InputMappingFacet, isPromise, MulticastDelegate1, RemoveSubModelMessage } from "flavr-common";
import { busService } from "../../bus";
import { DEFAULT_KEY_ACTION_MAPPING } from "./DefaultMapping";
import { InputKeySpecial } from "./InputTypes";
const DEFAULT_ACTION_MAPPING = new Map();
DEFAULT_ACTION_MAPPING.set(0, "openTools");
class InputKeyData {
    constructor(key, action) {
        this.key = key;
        this.action = action;
        this.shift = false;
        this.ctrl = false;
        this.alt = false;
        this.cmd = false;
        this.isForDev = false;
    }
    addSpecial(special) {
        switch (special) {
            case InputKeySpecial.Shift:
                this.shift = true;
            case InputKeySpecial.Ctrl:
                this.ctrl = true;
            case InputKeySpecial.Alt:
                this.alt = true;
            case InputKeySpecial.Cmd:
                this.cmd = true;
        }
        return this;
    }
    asDev() {
        this.shift = true;
        this.isForDev = true;
    }
}
class InputAction {
    constructor(connection, callback, description) {
        this.connection = connection;
        this.callback = callback;
        this.description = description;
        this.keys = new Map();
    }
    addKey(key) {
        const keyData = new InputKeyData(key, this);
        this.keys.set(key, keyData);
        if (this.actionIdx === undefined) {
            if (this.pendingKeys === undefined) {
                this.pendingKeys = [];
            }
            this.pendingKeys.push(keyData);
        }
        else {
            // call after all key settings are set
            setTimeout(() => {
                if (this.actionIdx === undefined)
                    return;
                const msg = new InputActionKeyBindingsMessage(this.actionIdx, {});
                msg.keys[keyData.key] = {
                    state: ButtonSignal.RELEASED,
                    shift: keyData.shift,
                    ctrl: keyData.ctrl,
                    alt: keyData.alt,
                    cmd: keyData.cmd,
                    isDev: keyData.isForDev
                };
                this.connection.send(msg);
            }, 0);
        }
        return keyData;
    }
    _onRegistered(actionIndex) {
        this.actionIdx = actionIndex;
        if (this.pendingKeys) {
            const msg = new InputActionKeyBindingsMessage(this.actionIdx, {});
            for (const data of this.pendingKeys) {
                msg.keys[data.key] = {
                    state: ButtonSignal.RELEASED,
                    shift: data.shift,
                    ctrl: data.ctrl,
                    alt: data.alt,
                    cmd: data.cmd,
                    isDev: data.isForDev
                };
            }
            this.connection.send(msg);
            this.pendingKeys = undefined;
        }
    }
    _onCallback() {
        this.callback();
    }
}
class InputActionService {
    constructor() {
        this.connection = busService.connection("CoreInputService");
        this.onControllerButtonDelegate = new MulticastDelegate1();
        this.onActionDelegate = new Map();
        this.onAxesDelegate = new Map();
        this.inputMappingMap = new Map();
        this.actions = new Map();
        this.handleInputActionMessage = (msg) => {
            if (msg.uid !== undefined) {
                const data = this.inputMappingMap.get(msg.uid);
                if (!data)
                    return;
                const action = data.mapping.actions[msg.actionIdx];
                if (!action)
                    return;
                const delegate = this.onActionDelegate.get(action.name);
                if (!delegate)
                    return;
                for (const callback of delegate) {
                    this.catchReturnment(callback(action.name));
                }
                return;
            }
            // default mapping
            const actionName = DEFAULT_KEY_ACTION_MAPPING.get(msg.actionIdx);
            if (!actionName) {
                console.error(`Can't find default action for index ${msg.actionIdx}`);
                return;
            }
            const delegate = this.onActionDelegate.get(actionName);
            if (!delegate)
                return;
            try {
                for (const callback of delegate) {
                    this.catchReturnment(callback(actionName));
                }
            }
            catch (error) {
                console.error(`Input action ${actionName}`, error);
            }
        };
        this.handleInputMappingFacet = (uid, facet, meta) => {
            this.inputMappingMap.set(uid, {
                createdTime: meta.timeStamp,
                mapping: facet.params
            });
        };
        this.handleRemoveSubModelMessage = (msg, meta) => {
            const data = this.inputMappingMap.get(msg.uid);
            if (!data)
                return;
            if (data.createdTime > meta.timeStamp)
                return;
            this.inputMappingMap.delete(msg.uid);
        };
        this.handleInputAxisValueMessage = (msg) => {
            if (msg.uid === undefined)
                return;
            const data = this.inputMappingMap.get(msg.uid);
            if (!data)
                return;
            const axis = data.mapping.axes[msg.axisIndex];
            if (!axis)
                return;
            const delegate = this.onAxesDelegate.get(axis.name);
            if (!delegate)
                return;
            try {
                for (const callback of delegate) {
                    this.catchReturnment(callback(msg.value));
                }
            }
            catch (error) {
                console.error(`Input axis ${axis.name}`, error);
            }
        };
        this.handleInputActionTriggeredMessage = (msg) => {
            const action = this.actions.get(msg.actionIdx);
            if (!action)
                return;
            action._onCallback();
        };
        this.connection.subscribeByType(InputActionMessage, this.handleInputActionMessage);
        this.connection.subscribeByFacetType(InputMappingFacet, this.handleInputMappingFacet);
        this.connection.subscribeByType(RemoveSubModelMessage, this.handleRemoveSubModelMessage);
        this.connection.subscribeByType(InputAxisValueMessage, this.handleInputAxisValueMessage);
        this.connection.subscribeByType(InputActionTriggeredMessage, this.handleInputActionTriggeredMessage);
    }
    registerAction(name, callback, description) {
        const action = new InputAction(this.connection, callback, description);
        this.connection
            .askByMessageType(new RegisterInputActionMessage(name, description), ResponseRegisterInputActionMessage)
            .then((msg) => {
            this.actions.set(msg.actionIdx, action);
            action._onRegistered(msg.actionIdx);
        })
            .catch((error) => {
            console.error("registerAction", error);
        });
        return action;
    }
    unregisterAction(name) {
        this.connection.send(new UnregisterInputActionMessage(name));
    }
    subscribeAction(subscribeActionName, callback) {
        let delegate = this.onActionDelegate.get(subscribeActionName);
        if (!delegate) {
            delegate = new Set();
            this.onActionDelegate.set(subscribeActionName, delegate);
        }
        delegate.add(callback);
    }
    unsubscribeAction(callback) {
        for (const delegate of this.onActionDelegate.values()) {
            delegate.delete(callback);
        }
    }
    subscribeAxis(axisName, callback) {
        let delegate = this.onAxesDelegate.get(axisName);
        if (!delegate) {
            delegate = new Set();
            this.onAxesDelegate.set(axisName, delegate);
        }
        delegate.add(callback);
    }
    unsubscribeAxis(callback) {
        for (const delegate of this.onAxesDelegate.values()) {
            delegate.delete(callback);
        }
    }
    catchReturnment(returnment) {
        if (isPromise(returnment)) {
            Promise.resolve(returnment).catch((error) => {
                console.error(error);
            });
        }
    }
}
export const inputActionService = new InputActionService();
